// ShareTemplateEngine — extração leve de conteúdo compartilhável.
// Zero IA, apenas string manipulation. Recebe os props de conteúdo
// (que podem vir de qualquer tela: devocional, sermão, plano, etc.)
// e retorna o payload canônico para o backend + card generator.

const clean = (s) => (s || "").toString().replace(/\s+/g, " ").trim();

/**
 * @param {object} raw
 * @param {"devocional"|"sermon"|"plan"|"verse"|"prayer"|"study"} raw.contentType
 * @param {string} raw.contentId
 * @param {string} [raw.title]
 * @param {string} [raw.mainText]
 * @param {string} [raw.verse]
 * @param {string} [raw.verseRef]
 * @param {string} [raw.application]
 * @param {string} [raw.question]
 * @param {string} [raw.prayer]
 * @param {string} [raw.theme]
 */
export function extractShareData(raw = {}) {
  const title = clean(raw.title);
  const verse = clean(raw.verse);
  const verseRef = clean(raw.verseRef);
  const application = clean(raw.application);
  const prayer = clean(raw.prayer);
  const theme = clean(raw.theme);

  // main phrase: preferimos mainText explícito; senão pegamos a primeira
  // frase curta (~60-180 chars) do primeiro campo disponível.
  let mainPhrase = clean(raw.mainText);
  if (!mainPhrase) {
    const source = [raw.reflection, raw.application, raw.mainText, verse].map(clean).find(Boolean);
    if (source) {
      const sentences = source.split(/(?<=[.!?…])\s+/);
      const impact = sentences.find((s) => s.length >= 40 && s.length <= 200) || sentences[0];
      mainPhrase = clean(impact || source.slice(0, 180));
    }
  }
  if (!mainPhrase && verse) mainPhrase = verse;

  // question: 1ª frase terminada em '?'
  let question = clean(raw.question);
  if (!question) {
    const src = clean(raw.reflection) || application;
    const m = src && src.match(/[^.!?…]*\?/);
    if (m) question = clean(m[0]);
  }

  return {
    title: title || "Reflexão do dia",
    main_phrase: mainPhrase.slice(0, 500),
    verse: verse.slice(0, 500),
    verse_ref: verseRef.slice(0, 80),
    theme: theme.slice(0, 60),
    question: question.slice(0, 300),
    application: application.slice(0, 500),
    prayer: prayer.slice(0, 500),
    // Fase C.1 — tema semanal ativo da landing (silencioso se null).
    weekly_theme_slug: clean(raw.weekly_theme_slug) || null,
    weekly_theme_name: clean(raw.weekly_theme_name) || null,
    weekly_theme_accent: clean(raw.weekly_theme_accent) || null,
  };
}

export function buildWhatsappText({ title, main_phrase, verse_ref, application }, publicUrl) {
  const lines = [];
  if (title) lines.push(`✝️ ${title}`);
  if (main_phrase) lines.push(`"${main_phrase}"`);
  if (verse_ref) lines.push(`📖 ${verse_ref}`);
  if (application) {
    const short = application.length > 180 ? application.slice(0, 177) + "..." : application;
    lines.push(short);
  }
  lines.push("Continue lendo 👇");
  lines.push(publicUrl);
  return lines.filter(Boolean).join("\n\n");
}

// --------- Templates inteligentes por tema (P4) ---------
// String match determinístico (case/accent-insensitive). Sem IA.
// Se nada bater, retorna "contemplacao" (default).

const _norm = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const TEMPLATE_KEYWORDS = {
  esperanca: ["esperanca", "esperança", "fe", "fé", "promessa", "prometido", "confianca", "confiança"],
  oracao: ["oracao", "oração", "intercessao", "intercessão", "intercedendo", "clamor", "orar", "ore por"],
  perdao: ["perdao", "perdão", "perdoar", "reconciliacao", "reconciliação", "graca", "graça", "misericordia", "misericórdia"],
  familia: ["familia", "família", "casamento", "esposa", "esposo", "filhos", "filha", "filho", "pais", "mae", "mãe", "pai"],
  paz: ["ansiedade", "ansioso", "paz", "descanso", "descansar", "aquietai", "aquietar", "calma", "sossego", "tranquilidade"],
  proposito: ["identidade", "proposito", "propósito", "chamado", "chamada", "vocacao", "vocação", "missao", "missão", "enviado"],
};

const TEMPLATE_ORDER = ["oracao", "perdao", "familia", "paz", "proposito", "esperanca"];

/**
 * Seleciona template com base em theme + title + verse_ref + main_phrase (primeiras 50 palavras).
 * Ordem de prioridade: oração > perdão > família > paz > propósito > esperança > contemplação.
 *
 * P4/C.1 — Se `data.weekly_theme_slug` for fornecido pela landing (via useDaily),
 * ele TEM prioridade sobre a inferência por keywords. Mantém coerência editorial
 * automática entre landing e app: admin muda tema → todos os novos shares refletem.
 *
 * @param {object} data payload já extraído por extractShareData
 * @returns {"contemplacao"|"esperanca"|"oracao"|"perdao"|"familia"|"paz"|"proposito"}
 */
export function selectTemplateFromTheme(data = {}) {
  // Fase C.1 — landing manda o tom.
  const themeSlug = (data.weekly_theme_slug || "").toString().toLowerCase().trim();
  if (themeSlug && TEMPLATE_KEYWORDS[themeSlug]) return themeSlug;
  const legacyMap = { fe: "esperanca", gratidao: "esperanca", identidade: "proposito" };
  if (themeSlug && legacyMap[themeSlug]) return legacyMap[themeSlug];

  const parts = [data.theme, data.title, data.verse_ref, data.main_phrase];
  const haystack = _norm(parts.filter(Boolean).join(" ").split(/\s+/).slice(0, 50).join(" "));
  if (!haystack) return "contemplacao";
  for (const tpl of TEMPLATE_ORDER) {
    const kws = TEMPLATE_KEYWORDS[tpl];
    if (kws.some((kw) => haystack.includes(_norm(kw)))) return tpl;
  }
  return "contemplacao";
}

export const ALL_TEMPLATES = [
  { id: "contemplacao", label: "Contemplação" },
  { id: "esperanca", label: "Esperança" },
  { id: "oracao", label: "Oração" },
  { id: "perdao", label: "Perdão" },
  { id: "familia", label: "Família" },
  { id: "paz", label: "Paz" },
  { id: "proposito", label: "Propósito" },
];
